<template>
  <div id="check-step">
    <div class="form">
      <center class="name-label">{{ $t('CLIENT_INFO') }}</center>
      <client-form ref="clientForm" v-model="clientFormValid" :edit="clientFormEdit"/>
      <div v-if="!(assetFormEdit || authorizedFormEdit || realestateFormEdit || contractFormEdit)" class="d-flex justify-content-end">
        <primary-button v-if="!clientFormEdit" @click="editClientForm" :icon="faPen" class="button" white rounded-corner :text="$t('EDIT')" />
        <primary-button v-else :disabled="!clientFormValid" @click="acceptClientForm" class="button" rounded-corner :text="$t('ACCEPT')" />
      </div>
    </div>
    <div class="form">
      <center class="name-label">{{ $t('COLLATERAL_INFO') }}</center>
      <asset-form ref="assetForm" v-model="assetFormValid" :edit="assetFormEdit"/>
      <div v-if="!(clientFormEdit || authorizedFormEdit || realestateFormEdit || contractFormEdit)" class="d-flex justify-content-end">
        <primary-button v-if="!assetFormEdit" @click="editAssetForm" :icon="faPen" class="button" white rounded-corner :text="$t('EDIT')" />
        <primary-button v-else :disabled="!assetFormValid" @click="acceptAssetForm" class="button" rounded-corner :text="$t('ACCEPT')" />
      </div>
    </div>
    <div v-if="isAuthorized" class="form">
      <center class="name-label">{{ $t('AUTHORIZATION_CONTRACT') }}</center>
      <authorized-form ref="authorizedForm" v-model="authorizedFormValid" :edit="authorizedFormEdit"/>
      <div v-if="!(clientFormEdit || assetFormEdit || realestateFormEdit || contractFormEdit)" class="d-flex justify-content-end">
        <primary-button v-if="!authorizedFormEdit" @click="editAuthorizedForm" :icon="faPen" class="button" white rounded-corner :text="$t('EDIT')" />
        <primary-button v-else :disabled="!authorizedFormValid" @click="acceptAuthorizedForm" class="button" rounded-corner :text="$t('ACCEPT')" />
      </div>
    </div>
    <div v-if="isPurchased" class="form">
      <center class="name-label">{{ $t('PURCHASE_CONTRACT') }}</center>
      <realestate-form ref="realestateForm" v-model="realestateFormValid" :edit="realestateFormEdit"/>
      <div v-if="!(clientFormEdit || assetFormEdit || authorizedFormEdit || contractFormEdit)" class="d-flex justify-content-end">
        <primary-button v-if="!realestateFormEdit" @click="editRealestateForm" :icon="faPen" class="button" white rounded-corner :text="$t('EDIT')" />
        <primary-button v-else :disabled="!realestateFormValid" @click="realestateFormEdit = false" class="button" rounded-corner :text="$t('ACCEPT')" />
      </div>
    </div>
    <div class="form">
      <center class="name-label">{{ $t('CONTRACT_DETAIL') }}</center>
      <contract-form ref="contractForm" v-model="contractFormValid" :edit="contractFormEdit"/>
      <div v-if="!(clientFormEdit || assetFormEdit || authorizedFormEdit || realestateFormEdit)" class="d-flex justify-content-end">
        <primary-button v-if="!contractFormEdit" @click="back" :icon="faPen" class="button" independence rounded-corner :text="$t('CANCEL')" />
        <primary-button v-if="!contractFormEdit" @click="editContractForm" :icon="faPen" class="button" white rounded-corner :text="$t('EDIT')" />
        <primary-button v-else :disabled="!contractFormValid" @click="acceptContractForm" class="button" rounded-corner :text="$t('ACCEPT')" />
        <primary-button v-if="!contractFormEdit" @click="next" :icon="faPen" class="button" rounded-corner :text="$t('CONFIRM')" />
      </div>
    </div>
    <confirm-modal />
  </div>
</template>

<script>
import { ref } from '@vue/composition-api';
import PrimaryButton from '@/components/PrimaryButton';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { createNamespacedHelpers } from 'vuex-composition-helpers';

import ConfirmModal from '@/components/ConfirmModal';
import ClientForm from '../../Forms/ClientForm';
import AssetForm from '../../Forms/AssetForm';
import AuthorizedForm from '../../Forms/AuthorizedForm';
import RealestateForm from '../../Forms/RealestateForm';
import ContractForm from '../../Forms/ContractForm';

const { useState } = createNamespacedHelpers('contractNew');
const { useState: useAppState } = createNamespacedHelpers('app');
const { useActions: useModalActions } = createNamespacedHelpers('modal');
const { useActions: useConfirmActions } = createNamespacedHelpers('confirm');

export default {
  name: 'CheckStep',
  components: {
    ConfirmModal,
    ClientForm,
    AssetForm,
    AuthorizedForm,
    RealestateForm,
    ContractForm,
    PrimaryButton,
  },
  setup(props, { root, emit }) {
    const { order } = useState(['order']);
    const { assetTypes } = useAppState(['assetTypes']);
    const { toggleConfirmModal } = useModalActions(['toggleConfirmModal']);
    const { setTitleModal, setOnConfirm } = useConfirmActions(['setTitleModal', 'setOnConfirm']);

    const assetType = assetTypes.value.find((item) => item.description === order.value.assetType);
    const isPurchased = assetType.name === 'Realestate';
    const isAuthorized = ['Car', 'Bike', 'Truck'].includes(assetType.name);

    const clientForm = ref(null);
    const clientFormEdit = ref(false);
    const clientFormValid = ref(true);
    const editClientForm = () => {
      setTitleModal(root.$t('CONFIRM_EDIT_CLIENT_INFO'));
      setOnConfirm(() => { clientFormEdit.value = true; });
      toggleConfirmModal();
    };
    const acceptClientForm = () => {
      clientForm.value.setData();
      clientFormEdit.value = false;
    };

    const assetForm = ref(null);
    const assetFormEdit = ref(false);
    const assetFormValid = ref(true);
    const editAssetForm = () => {
      setTitleModal(root.$t('CONFIRM_EDIT_COLLATERAL_INFO'));
      setOnConfirm(() => { assetFormEdit.value = true; });
      toggleConfirmModal();
    };
    const acceptAssetForm = () => {
      assetForm.value.setData();
      assetFormEdit.value = false;
    };

    const authorizedForm = ref(null);
    const authorizedFormEdit = ref(false);
    const authorizedFormValid = ref(true);
    const editAuthorizedForm = () => {
      setTitleModal(root.$t('CONFIRM_EDIT_AUTHORIZATION_CONTRACT'));
      setOnConfirm(() => { authorizedFormEdit.value = true; });
      toggleConfirmModal();
    };
    const acceptAuthorizedForm = () => {
      authorizedForm.value.setData();
      authorizedFormEdit.value = false;
    };

    const realestateForm = ref(null);
    const realestateFormEdit = ref(false);
    const realestateFormValid = ref(true);
    const editRealestateForm = () => {
      setTitleModal(root.$t('CONFIRM_EDIT_PURCHASE_CONTRACT'));
      setOnConfirm(() => { realestateFormEdit.value = true; });
      toggleConfirmModal();
    };
    const acceptRealestateForm = () => {
      realestateForm.value.setData();
      realestateFormEdit.value = false;
    };

    const contractForm = ref(null);
    const contractFormEdit = ref(false);
    const contractFormValid = ref(true);
    const editContractForm = () => {
      setTitleModal(root.$t('CONFIRM_EDIT_CONTRACT_DETAIL'));
      setOnConfirm(() => { contractFormEdit.value = true; });
      toggleConfirmModal();
    };
    const acceptContractForm = () => {
      contractForm.value.setData();
      contractFormEdit.value = false;
    };

    const back = () => { root.$router.back(); };
    const next = () => emit('next');

    return {
      clientForm,
      clientFormEdit,
      clientFormValid,
      editClientForm,
      acceptClientForm,
      assetForm,
      assetFormEdit,
      assetFormValid,
      editAssetForm,
      acceptAssetForm,
      authorizedForm,
      authorizedFormEdit,
      authorizedFormValid,
      editAuthorizedForm,
      acceptAuthorizedForm,
      realestateForm,
      realestateFormEdit,
      realestateFormValid,
      editRealestateForm,
      acceptRealestateForm,
      contractForm,
      contractFormEdit,
      contractFormValid,
      editContractForm,
      acceptContractForm,
      isPurchased,
      isAuthorized,
      faPen,
      back,
      next,
    };
  },
};
</script>

<style lang='scss' scoped>
@import "~@/assets/scss/colors";

#check-step {
  .name-label {
    font-size: 28px;
    text-decoration: underline;
    text-transform: capitalize;
    color: $independence;
    margin-bottom: 32px;
    font-weight: bold;
  }

  .form {
    padding: 28px 80px 33px;
    background-color: $white;
    border-radius: 8px;
    margin-bottom: 30px;
  }

  .button {
    margin-left: 20px;
    box-shadow: 0px 3px 6px $silver;
  }
}
</style>
